import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Clock,
  AlertTriangle,
  X,
  RefreshCw,
  Megaphone,
  Loader2,
  CheckCircle,
  Users,
  Send,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SessionManagement({ user }) {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [activeTerms, setActiveTerms] = useState([]);
  const [termFilter, setTermFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Modal states
  const [cancelModal, setCancelModal] = useState({ open: false, session: null });
  const [locationModal, setLocationModal] = useState({ open: false, session: null });
  const [rescheduleModal, setRescheduleModal] = useState({ open: false, session: null, exception: null });
  const [announceModal, setAnnounceModal] = useState({ open: false, session: null });
  
  // Form states
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Every live programme, not just one: the academy runs several terms side by side
      const termsRes = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      const today = new Date().toISOString().slice(0, 10);
      const usable = (termsRes.data || []).filter(
        (t) => t.status !== "archived" && !t.is_archived && !t.is_hidden
      );
      // A term counts as live if it is marked active OR today falls inside its
      // dates — a term nobody remembered to activate still has real sessions.
      let live = usable.filter(
        (t) => t.status === "active" || t.is_active
               || (String(t.start_date || "").slice(0, 10) <= today
                   && today <= String(t.end_date || "").slice(0, 10))
      );
      if (!live.length) live = usable;
      setActiveTerms(live);

      const allSessions = [];
      const allExceptions = [];
      for (const term of live) {
        const [sRes, eRes] = await Promise.all([
          axios.get(`${API}/academy/terms/${term.id}/sessions`, { withCredentials: true }),
          axios.get(`${API}/academy/session-exceptions?term_id=${term.id}`, { withCredentials: true })
            .catch(() => ({ data: { exceptions: [] } })),
        ]);
        (sRes.data || []).forEach((s) =>
          allSessions.push({ ...s, term_name: term.name, term_id: term.id })
        );
        (eRes.data?.exceptions || []).forEach((e) =>
          allExceptions.push({ ...e, term_name: term.name })
        );
      }
      allSessions.sort((a, b) => (a.day_of_week - b.day_of_week) ||
        String(a.start_time || "").localeCompare(String(b.start_time || "")));
      setSessions(allSessions);
      setExceptions(allExceptions);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  // An age group label for a weekly session: whatever the template carries,
  // otherwise the programme it belongs to.
  const groupOf = (s) =>
    s.age_group_name || s.age_group ||
    (s.min_age && s.max_age ? `U${s.max_age}` : null) ||
    s.team_name || s.term_name || "Unassigned";

  const ageGroups = Array.from(new Set(sessions.map(groupOf))).sort();
  const visibleSessions = sessions.filter(
    (s) => (termFilter === "all" || s.term_id === termFilter) &&
           (groupFilter === "all" || groupOf(s) === groupFilter)
  );

  const dayNames = {
    1: "Monday", 2: "Tuesday", 3: "Wednesday", 
    4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday"
  };

  // Cancel Session Handler
  const handleCancelSession = async (formData) => {
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/academy/session-templates/${cancelModal.session.id}/cancel`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(`Session cancelled! ${response.data.notifications_sent} notifications sent.`);
      setCancelModal({ open: false, session: null });
      fetchData();
    } catch (err) {
      console.error("Error cancelling session:", err);
      toast.error(err.response?.data?.detail || "Failed to cancel session");
    } finally {
      setSubmitting(false);
    }
  };

  // Change Location Handler
  const handleChangeLocation = async (formData) => {
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/academy/session-templates/${locationModal.session.id}/change-location`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(`Location updated! ${response.data.notifications_sent} notifications sent.`);
      setLocationModal({ open: false, session: null });
      fetchData();
    } catch (err) {
      console.error("Error changing location:", err);
      toast.error(err.response?.data?.detail || "Failed to change location");
    } finally {
      setSubmitting(false);
    }
  };

  // Reschedule Handler
  const handleReschedule = async (formData) => {
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/academy/session-templates/${rescheduleModal.session.id}/reschedule`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(`Session rescheduled! ${response.data.notifications_sent} notifications sent.`);
      setRescheduleModal({ open: false, session: null, exception: null });
      fetchData();
    } catch (err) {
      console.error("Error rescheduling session:", err);
      toast.error(err.response?.data?.detail || "Failed to reschedule session");
    } finally {
      setSubmitting(false);
    }
  };

  // Announce Handler
  const handleAnnounce = async (formData) => {
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/academy/session-templates/${announceModal.session.id}/announce`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(`Announcement sent to ${response.data.notifications_sent} participants!`);
      setAnnounceModal({ open: false, session: null });
    } catch (err) {
      console.error("Error sending announcement:", err);
      toast.error(err.response?.data?.detail || "Failed to send announcement");
    } finally {
      setSubmitting(false);
    }
  };

  // Remove Exception Handler
  const handleRemoveException = async (exceptionId) => {
    if (!window.confirm("Are you sure you want to remove this exception? This will restore the original session.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/academy/session-exceptions/${exceptionId}`, { withCredentials: true });
      toast.success("Exception removed");
      fetchData();
    } catch (err) {
      console.error("Error removing exception:", err);
      toast.error("Failed to remove exception");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-900">
      {/* Header */}
      <div className="bg-slate-950 border-b border-white/5 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/academy">
            <Button variant="ghost" className="text-white hover:bg-slate-900/50/20 mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Academy
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Weekly Schedule</h1>
          <p className="text-blue-200">The weekly slots — cancel, reschedule, or update them</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 sm:p-6">
        {activeTerms.length === 0 ? (
          <Card className="p-8 text-center" data-testid="session-mgmt-empty">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold">No programmes to show</h3>
            <p className="text-slate-400">
              Every term is archived or hidden. Un-archive one in Term Management, or create
              the new term, and its weekly slots will appear here.
            </p>
            <Link to="/academy/term-management">
              <Button className="mt-4" data-testid="session-mgmt-terms-link">
                Open Term Management
              </Button>
            </Link>
          </Card>
        ) : (
          <>
            {/* Programme + age group filters */}
            <Card className="mb-6 bg-slate-900/50 border-slate-700">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs text-slate-400">Programme</Label>
                    <Select value={termFilter} onValueChange={setTermFilter}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1" data-testid="session-mgmt-term-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All live programmes ({activeTerms.length})</SelectItem>
                        {activeTerms.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs text-slate-400">Age group</Label>
                    <Select value={groupFilter} onValueChange={setGroupFilter}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1" data-testid="session-mgmt-group-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All age groups</SelectItem>
                        {ageGroups.map((g) => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={fetchData} className="h-10" data-testid="session-mgmt-refresh">
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-3" data-testid="session-mgmt-count">
                  {visibleSessions.length} weekly session{visibleSessions.length === 1 ? "" : "s"} shown
                  {termFilter === "all" && ` across ${activeTerms.length} programme(s)`}
                </p>
              </CardContent>
            </Card>

            {/* Session Exceptions / Alerts */}
            {exceptions.length > 0 && (
              <Card className="mb-6 border-white/5 bg-yellow-500/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-yellow-400 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Active Session Changes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {exceptions.map((exc) => (
                      <div
                        key={exc.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          exc.exception_type === "cancelled"
                            ? "bg-red-500/20 border-l-4 border-red-500"
                            : exc.exception_type === "rescheduled"
                            ? "bg-blue-500/20 border-l-4 border-blue-500"
                            : "bg-yellow-500/20 border-l-4 border-yellow-500"
                        }`}
                      >
                        <div>
                          <p className="font-medium">
                            {exc.exception_type === "cancelled" && "❌ CANCELLED: "}
                            {exc.exception_type === "rescheduled" && "📅 RESCHEDULED: "}
                            {exc.exception_type === "location_change" && "📍 LOCATION CHANGE: "}
                            {exc.session_name}
                          </p>
                          <p className="text-sm text-slate-400">
                            {exc.affected_date}
                            {exc.new_date && ` → ${exc.new_date}`}
                            {exc.new_location && ` at ${exc.new_location}`}
                          </p>
                          {exc.reason_details && (
                            <p className="text-xs text-slate-400 mt-1">{exc.reason_details}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveException(exc.id)}
                          className="text-slate-400 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sessions List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Weekly Sessions                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {visibleSessions.length === 0 ? (
                    <p className="text-center text-slate-400 py-8" data-testid="session-mgmt-no-sessions">
                      No sessions match these filters
                    </p>
                  ) : (
                    visibleSessions.map((session) => (
                      <div
                        key={session.id}
                        className="p-4 bg-[#0a0f1a] rounded-lg hover:bg-slate-800/50 transition-colors"
                        data-testid={`session-mgmt-row-${session.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-medium">
                              {session.name || `${dayNames[session.day_of_week]} Training`}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {dayNames[session.day_of_week]}s
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {session.start_time} - {session.end_time}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {session.location || "TBD"}
                              </span>
                              {session.age_group && (
                                <Badge variant="outline">{session.age_group}</Badge>
                              )}
                              {session.term_name && (
                                <Badge variant="outline" className="border-blue-500/40 text-blue-300">
                                  {session.term_name}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                            onClick={() => setCancelModal({ open: true, session })}
                          >
                            <X className="w-4 h-4 mr-1" /> Cancel Session
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-yellow-500 border-white/5 hover:bg-yellow-500/10"
                            onClick={() => setLocationModal({ open: true, session })}
                          >
                            <MapPin className="w-4 h-4 mr-1" /> Change Location
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                            onClick={() => setRescheduleModal({ open: true, session, exception: null })}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" /> Reschedule
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
                            onClick={() => setAnnounceModal({ open: true, session })}
                          >
                            <Megaphone className="w-4 h-4 mr-1" /> Announce
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Cancel Session Modal */}
      {cancelModal.open && cancelModal.session && (
        <CancelSessionModal
          key={`cancel-${cancelModal.session?.id}`}
          open={cancelModal.open}
          session={cancelModal.session}
          onClose={() => setCancelModal({ open: false, session: null })}
          onSubmit={handleCancelSession}
          submitting={submitting}
        />
      )}

      {/* Change Location Modal */}
      {locationModal.open && locationModal.session && (
        <LocationChangeModal
          key={`location-${locationModal.session?.id}`}
          open={locationModal.open}
          session={locationModal.session}
          onClose={() => setLocationModal({ open: false, session: null })}
          onSubmit={handleChangeLocation}
          submitting={submitting}
        />
      )}

      {/* Reschedule Modal */}
      {rescheduleModal.open && rescheduleModal.session && (
        <RescheduleModal
          key={`reschedule-${rescheduleModal.session?.id}-${rescheduleModal.exception?.id || 'new'}`}
          open={rescheduleModal.open}
          session={rescheduleModal.session}
          exception={rescheduleModal.exception}
          onClose={() => setRescheduleModal({ open: false, session: null, exception: null })}
          onSubmit={handleReschedule}
          submitting={submitting}
        />
      )}

      {/* Announce Modal */}
      {announceModal.open && announceModal.session && (
        <AnnounceModal
          key={`announce-${announceModal.session?.id}-${Date.now()}`}
          open={announceModal.open}
          session={announceModal.session}
          onClose={() => setAnnounceModal({ open: false, session: null })}
          onSubmit={handleAnnounce}
          submitting={submitting}
        />
      )}
    </div>
  );
}

// Cancel Session Modal Component
function CancelSessionModal({ open, session, onClose, onSubmit, submitting }) {
  const [affectedDate, setAffectedDate] = useState("");
  const [reasonCode, setReasonCode] = useState("weather");
  const [reasonDetails, setReasonDetails] = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const [grantCredits, setGrantCredits] = useState(true);
  const [grantingCredits, setGrantingCredits] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async () => {
    if (!affectedDate) {
      toast.error("Please select a date");
      return;
    }

    // First cancel the session
    await onSubmit({
      affected_date: affectedDate,
      reason_code: reasonCode,
      reason_details: reasonDetails,
      send_notification: sendNotification,
    });

    // Then grant credits if selected
    if (grantCredits && session?.id) {
      setGrantingCredits(true);
      try {
        const reasonLabels = {
          weather: "Weather cancellation",
          venue_unavailable: "Venue unavailable",
          coach_unavailable: "Coach unavailable",
          public_holiday: "Public holiday",
          other: reasonDetails || "Session cancelled"
        };
        
        const response = await axios.post(
          `${API}/academy/admin/credits/bulk-grant-for-cancellation`,
          null,
          { 
            params: {
              session_id: session.id,
              session_date: affectedDate,
              reason: "session_cancelled",
              reason_details: reasonLabels[reasonCode]
            },
            withCredentials: true 
          }
        );
        toast.success(`✅ Granted 1 credit to ${response.data.players_credited} players`);
      } catch (err) {
        console.error("Error granting credits:", err);
        toast.error("Session cancelled but failed to grant credits");
      } finally {
        setGrantingCredits(false);
      }
    }
  };

  const dayNames = {1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday"};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <X className="w-5 h-5" /> Cancel Session
          </DialogTitle>
        </DialogHeader>

        {session && (
          <div className="space-y-4">
            <div className="bg-[#0a0f1a] p-3 rounded-lg">
              <p className="font-medium">{session.name || `${dayNames[session.day_of_week]} Training`}</p>
              <p className="text-sm text-slate-400">
                {dayNames[session.day_of_week]}s, {session.start_time} - {session.end_time}
              </p>
            </div>

            <div>
              <Label>Date to Cancel *</Label>
              <Input
                type="date"
                value={affectedDate}
                onChange={(e) => setAffectedDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Reason *</Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weather">Weather conditions</SelectItem>
                  <SelectItem value="venue_unavailable">Venue unavailable</SelectItem>
                  <SelectItem value="coach_unavailable">Coach unavailable</SelectItem>
                  <SelectItem value="public_holiday">Public holiday</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Additional Details</Label>
              <Textarea
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                placeholder="Any additional information for parents..."
                className="mt-1"
              />
            </div>

            <div className="space-y-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="notify"
                  checked={sendNotification}
                  onCheckedChange={setSendNotification}
                />
                <label htmlFor="notify" className="text-sm">
                  📧 Send notification to all enrolled parents
                </label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="grantCredits"
                  checked={grantCredits}
                  onCheckedChange={setGrantCredits}
                />
                <label htmlFor="grantCredits" className="text-sm flex items-center gap-1">
                  🎫 <span className="text-emerald-400 font-medium">Grant 1 session credit</span> to all enrolled players
                </label>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || grantingCredits}
            className="bg-red-600 hover:bg-red-700"
          >
            {(submitting || grantingCredits) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {grantingCredits ? "Granting Credits..." : "Cancel Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Location Change Modal Component
function LocationChangeModal({ open, session, onClose, onSubmit, submitting }) {
  const [newLocation, setNewLocation] = useState(session?.location || "");
  const [affectedDate, setAffectedDate] = useState("");
  const [isPermanent, setIsPermanent] = useState(false);
  const [reason, setReason] = useState("");
  const [sendNotification, setSendNotification] = useState(true);

  const handleSubmit = () => {
    if (!newLocation.trim()) {
      toast.error("Please enter a new location");
      return;
    }
    if (!isPermanent && !affectedDate) {
      toast.error("Please select a date or mark as permanent");
      return;
    }
    onSubmit({
      new_location: newLocation,
      affected_date: isPermanent ? null : affectedDate,
      is_permanent: isPermanent,
      reason,
      send_notification: sendNotification,
    });
  };

  const dayNames = {1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday"};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-500">
            <MapPin className="w-5 h-5" /> Change Location
          </DialogTitle>
        </DialogHeader>

        {session && (
          <div className="space-y-4">
            <div className="bg-[#0a0f1a] p-3 rounded-lg">
              <p className="font-medium">{session.name || `${dayNames[session.day_of_week]} Training`}</p>
              <p className="text-sm text-slate-400">
                Current: {session.location || "TBD"}
              </p>
            </div>

            <div>
              <Label>New Location *</Label>
              <Input
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                placeholder="e.g., Dempsey Field B"
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="permanent"
                checked={isPermanent}
                onCheckedChange={setIsPermanent}
              />
              <label htmlFor="permanent" className="text-sm">
                Make this a permanent change
              </label>
            </div>

            {!isPermanent && (
              <div>
                <Label>Specific Date *</Label>
                <Input
                  type="date"
                  value={affectedDate}
                  onChange={(e) => setAffectedDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Field maintenance"
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-loc"
                checked={sendNotification}
                onCheckedChange={setSendNotification}
              />
              <label htmlFor="notify-loc" className="text-sm">
                Send notification to all enrolled parents
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Update Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Reschedule Modal Component
function RescheduleModal({ open, session, exception, onClose, onSubmit, submitting }) {
  const [originalDate, setOriginalDate] = useState(exception?.affected_date || "");
  const [newDate, setNewDate] = useState("");
  const [newStartTime, setNewStartTime] = useState(session?.start_time || "");
  const [newEndTime, setNewEndTime] = useState(session?.end_time || "");
  const [newLocation, setNewLocation] = useState(session?.location || "");
  const [reason, setReason] = useState("");
  const [sendNotification, setSendNotification] = useState(true);

  const handleSubmit = () => {
    if (!originalDate || !newDate) {
      toast.error("Please fill in both dates");
      return;
    }
    onSubmit({
      original_date: originalDate,
      new_date: newDate,
      new_start_time: newStartTime,
      new_end_time: newEndTime,
      new_location: newLocation,
      reason,
      send_notification: sendNotification,
    });
  };

  const dayNames = {1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday"};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-400">
            <RefreshCw className="w-5 h-5" /> Reschedule Session
          </DialogTitle>
        </DialogHeader>

        {session && (
          <div className="space-y-4">
            <div className="bg-[#0a0f1a] p-3 rounded-lg">
              <p className="font-medium">{session.name || `${dayNames[session.day_of_week]} Training`}</p>
              <p className="text-sm text-slate-400">
                {dayNames[session.day_of_week]}s, {session.start_time} - {session.end_time}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Original Date *</Label>
                <Input
                  type="date"
                  value={originalDate}
                  onChange={(e) => setOriginalDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>New Date *</Label>
                <Input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>New Start Time</Label>
                <Input
                  type="time"
                  value={newStartTime}
                  onChange={(e) => setNewStartTime(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>New End Time</Label>
                <Input
                  type="time"
                  value={newEndTime}
                  onChange={(e) => setNewEndTime(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>New Location</Label>
              <Input
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                placeholder="Same location or new"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Reason</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Make-up session for cancelled training"
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-resched"
                checked={sendNotification}
                onCheckedChange={setSendNotification}
              />
              <label htmlFor="notify-resched" className="text-sm">
                Send notification to all enrolled parents
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Announce Modal Component
function AnnounceModal({ open, session, onClose, onSubmit, submitting }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [announcementType, setAnnouncementType] = useState("general");
  const [affectedDate, setAffectedDate] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const handleSubmit = () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Please fill in title and message");
      return;
    }
    onSubmit({
      title,
      message,
      type: announcementType,
      affected_date: affectedDate || null,
      send_email: sendEmail,
    });
  };

  const dayNames = {1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday"};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-400">
            <Megaphone className="w-5 h-5" /> Send Announcement
          </DialogTitle>
        </DialogHeader>

        {session && (
          <div className="space-y-4">
            <div className="bg-[#0a0f1a] p-3 rounded-lg">
              <p className="font-medium">{session.name || `${dayNames[session.day_of_week]} Training`}</p>
              <p className="text-sm text-slate-400 flex items-center gap-1">
                <Users className="w-3 h-3" /> Will notify all enrolled parents and coaches
              </p>
            </div>

            <div>
              <Label>Announcement Type</Label>
              <Select value={announcementType} onValueChange={setAnnouncementType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="equipment">Equipment/Kit</SelectItem>
                  <SelectItem value="schedule_info">Schedule Info</SelectItem>
                  <SelectItem value="safety">Safety Notice</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Important: Bring shin guards"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Message *</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Your announcement message..."
                className="mt-1"
                rows={4}
              />
            </div>

            <div>
              <Label>For Specific Date (optional)</Label>
              <Input
                type="date"
                value={affectedDate}
                onChange={(e) => setAffectedDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
              />
              <label htmlFor="send-email" className="text-sm">
                Also send email notification
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send Announcement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
